import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableRowProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { LoadingButton } from '@mui/lab';
import { omit } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import Typography from '@mui/material/Typography';
import { UploadWidgetResult } from '@bytescale/upload-widget';
import { RemoveCircleOutline } from '@mui/icons-material';
import { KeyedMutator } from 'swr/_internal';
import { styled } from '@mui/system';
import {
  AccountData,
  AccountService,
  OrderingCutoffSettingsData,
  OrderingCutoffStrategy,
  OrderPageData,
  OrderPageTypes,
} from '../../../api';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { FormFieldColorPicker } from '../../formComponents/FormFieldColorPicker';
import { FormTextField } from '../../formComponents/FormTextField';
import { ImageUploadWidget } from '../../../emailTemplates/templateEditor/components/bytescale/ImageUploadWidget';
import { LocationPicker } from './LocationPicker';
import { FormCheckbox } from '../../formComponents/FormCheckBox';
import { FormTimePicker } from '../../formComponents/FormTimePicker';
import SharedMobileTimePicker from '../../../common/ui/SharedMobileTimePicker';

type SettingsSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

export type SettingsFormValues = {
  color: string;
  duration: number;
  ordering_cutoff_settings?: OrderingCutoffSettingsData;
  max_date_in_days: number;
  after_buffer_minutes: number;
  photo?: string;
  location_id?: string;
  wait_list_enabled?: boolean;
  default_time?: number;
  default_tickets_amount?: number;
};

const StyledTableRow = styled((props: TableRowProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  if (isSmallScreen) {
    return (
      <TableRow
        {...props}
        sx={{
          marginBottom: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      />
    );
  }

  return <TableRow {...props} />;
})({});

const StyledBoldHeader = styled((props: TableRowProps) => {
  return <Typography {...props} fontWeight="bold" />;
})({});
export const SettingsSection = ({
  orderPage,
  orderPageInitialValues,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: SettingsSectionProps) => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isEventOrderPage = orderPage.order_page_type === OrderPageTypes.EVENT;
  const schema = {
    color: yup.string().required(t('editOrderPage.yup.required')),
    duration: yup.number().required(t('editOrderPage.yup.required')).min(30, t('editOrderPage.yup.hasToBeMoreThan30')),
  };
  const privateOrderSpecificSchema = {
    max_date_in_days: yup
      .number()
      .required(t('editOrderPage.yup.required'))
      .positive(t('editOrderPage.yup.hasToBePoitive'))
      .lessThan(181, t('editOrderPage.yup.hasToBeLessThan180')),
    after_buffer_minutes: yup
      .number()
      .required(t('editOrderPage.yup.required'))
      .min(0, t('editOrderPage.yup.hasToBePoitive')),
  };
  const eventsSpecificSchema = {
    default_time: yup.number().min(0, t('editOrderPage.yup.hasToBePoitive')).nullable(),
    default_tickets_amount: yup.number().min(1, t('editOrderPage.yup.hasToBeMoreThan1')).required(),
  };
  const validationSchema = yup
    .object()
    .shape(isEventOrderPage ? { ...schema, ...eventsSpecificSchema } : { ...schema, ...privateOrderSpecificSchema });

  const initialValues: SettingsFormValues = {
    color: orderPage?.color || '#6577b3',
    duration: (orderPage?.duration || 0) / 60,
    ordering_cutoff_settings: orderPage.ordering_cutoff_settings || {
      strategy: OrderingCutoffStrategy.DAILY_CUTOFF,
      minimum_days_ahead: 0,
      cutoff_time_hour: 18,
      cutoff_time_minute: 0,
    },
    max_date_in_days: orderPage.max_date_in_days,
    after_buffer_minutes: orderPage.after_buffer_minutes,
    photo: orderPage.photo,
    location_id: orderPage.location_id,
    wait_list_enabled: orderPage.waiting_list?.enabled,
    default_time: orderPage.default_time,
    default_tickets_amount: orderPage.default_tickets_amount,
  };

  const handleSubmit = async (values: SettingsFormValues) => {
    try {
      posthog.capture('Editing settings', { section: 'settingsSection', settings: values });
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            ...values,
            id: orderPage.id,
            duration: (values.duration || 0) * 60,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
      setIsUpdatedSnackOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ values, isSubmitting, dirty, handleSubmit, setFieldValue }) => {
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={handleSubmit}>
            <Grid
              container
              flexDirection={isSmallScreen ? 'column-reverse' : 'row'}
              justifyContent={isSmallScreen ? undefined : 'space-between'}
              flexWrap="wrap"
              alignItems="flex-start">
              <Grid
                container
                justifyContent={isSmallScreen ? undefined : 'flex-end'}
                my={isSmallScreen ? 3 : undefined}>
                <LoadingButton
                  size="large"
                  loading={isSubmitting}
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!dirty || formIsDisabled}>
                  {t('editOrderPage.orderPageBufferSubmit')}
                </LoadingButton>
              </Grid>

              <TableContainer>
                <Table>
                  <TableBody>
                    <StyledTableRow>
                      <TableCell>
                        <StyledBoldHeader>{t('editOrderPage.address')}</StyledBoldHeader>
                      </TableCell>
                      <TableCell>
                        <LocationPicker
                          initialLocationId={initialValues.location_id}
                          onSelected={(selectedLocation) => setFieldValue('location_id', selectedLocation)}
                          withoutLabel
                        />
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <TableCell>
                        <StyledBoldHeader>{t('editOrderPage.orderPageColor')}</StyledBoldHeader>
                      </TableCell>
                      <TableCell>
                        <FormFieldColorPicker
                          tooltip={t('editOrderPage.orderPageColortooltip')}
                          disabled={formIsDisabled}
                          fieldName="color"
                        />
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <TableCell>
                        <StyledBoldHeader>{t('editOrderPage.photo')}</StyledBoldHeader>
                      </TableCell>
                      <TableCell>
                        {values.photo && values.photo.length > 0 ? (
                          <Grid container alignItems="center">
                            <Grid border="1px solid black">
                              <img
                                src={values.photo?.replace('raw', 'image').concat('?w=200&h=200&fit=crop')}
                                width={isSmallScreen ? '120px' : '200px'}
                                alt="logo"
                              />
                            </Grid>
                            <Grid>
                              <IconButton
                                onClick={() => setFieldValue('photo', undefined)}
                                style={{ width: '48px', height: '48px' }}>
                                <RemoveCircleOutline color={'disabled'} style={{ fontSize: '32px' }} />
                              </IconButton>
                            </Grid>
                          </Grid>
                        ) : (
                          <ImageUploadWidget
                            onImageUpload={(files: UploadWidgetResult[]) => setFieldValue('photo', files[0].fileUrl)}
                          />
                        )}
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <TableCell>
                        <StyledBoldHeader>{t('editOrderPage.orderingCutoffSettings.title')}</StyledBoldHeader>
                      </TableCell>
                      <TableCell>
                        <Grid container gap={2} alignItems="center" flexDirection="column">
                          <Grid container gap={1} alignItems="center">
                            <Grid item>
                              <Typography>{t('editOrderPage.orderingCutoffSettings.strategy')}</Typography>
                            </Grid>
                            <Grid item>
                              <Select
                                value={values.ordering_cutoff_settings?.strategy || OrderingCutoffStrategy.DAILY_CUTOFF}
                                onChange={(e) => setFieldValue('ordering_cutoff_settings.strategy', e.target.value)}
                                disabled={formIsDisabled}
                                size="small">
                                <MenuItem value={OrderingCutoffStrategy.DAILY_CUTOFF}>
                                  {t('editOrderPage.orderingCutoffSettings.dailyCutoff')}
                                </MenuItem>
                                <MenuItem value={OrderingCutoffStrategy.HOURS_BEFORE_START}>
                                  {t('editOrderPage.orderingCutoffSettings.hoursBeforeStart')}
                                </MenuItem>
                                <MenuItem value={OrderingCutoffStrategy.ALLOW_ON_THE_DAY_ORDERS}>
                                  {t('editOrderPage.orderingCutoffSettings.allowSameDayOrders')}
                                </MenuItem>
                              </Select>
                            </Grid>
                          </Grid>

                          {values.ordering_cutoff_settings?.strategy === OrderingCutoffStrategy.DAILY_CUTOFF && (
                            <Grid container gap={1} alignItems="center">
                              <Grid item>
                                <Typography>{t('editOrderPage.orderingCutoffSettings.mustBook')}</Typography>
                              </Grid>
                              <Grid item>
                                <FormTextField
                                  isFieldUpdate
                                  type="number"
                                  width={60}
                                  fieldName="ordering_cutoff_settings.minimum_days_ahead"
                                  disabled={formIsDisabled}
                                />
                              </Grid>
                              <Grid item>
                                <Typography>
                                  {t('editOrderPage.orderingCutoffSettings.daysAheadDailyCutoffAt')}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <SharedMobileTimePicker
                                  value={moment().set({
                                    hour: values.ordering_cutoff_settings?.cutoff_time_hour || 18,
                                    minute: values.ordering_cutoff_settings?.cutoff_time_minute || 0,
                                    second: 0,
                                    millisecond: 0,
                                  })}
                                  onChange={(newTime) => {
                                    if (newTime) {
                                      void setFieldValue('ordering_cutoff_settings.cutoff_time_hour', newTime.hour());
                                      void setFieldValue(
                                        'ordering_cutoff_settings.cutoff_time_minute',
                                        newTime.minute(),
                                      );
                                    }
                                  }}
                                  disabled={formIsDisabled}
                                  minutesStep={15}
                                />
                              </Grid>
                            </Grid>
                          )}

                          {values.ordering_cutoff_settings?.strategy === OrderingCutoffStrategy.HOURS_BEFORE_START && (
                            <Grid container gap={1} alignItems="center">
                              <Grid item>
                                <Typography>{t('editOrderPage.orderingCutoffSettings.bookingCloses')}</Typography>
                              </Grid>
                              <Grid item>
                                <FormTextField
                                  isFieldUpdate
                                  type="number"
                                  width={60}
                                  fieldName="ordering_cutoff_settings.hours_before_start"
                                  disabled={formIsDisabled}
                                />
                              </Grid>
                              <Grid item>
                                <Typography>
                                  {t('editOrderPage.orderingCutoffSettings.hoursBeforeWorkshopStarts')}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}

                          {values.ordering_cutoff_settings?.strategy ===
                            OrderingCutoffStrategy.ALLOW_ON_THE_DAY_ORDERS && (
                            <Grid container gap={1} alignItems="center">
                              <Grid item>
                                <Typography>
                                  {t('editOrderPage.orderingCutoffSettings.customersCanBookSameDay')}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}

                          <Grid container gap={1} alignItems="center">
                            <Grid item>
                              <Typography>{t('editOrderPage.orderingCutoffSettings.maxBookingWindow')}</Typography>
                            </Grid>
                            <Grid item>
                              <FormTextField
                                isFieldUpdate
                                width={70}
                                fieldName="max_date_in_days"
                                disabled={formIsDisabled}
                                type="number"
                              />
                            </Grid>
                            <Grid item>
                              <Typography>{t('editOrderPage.orderingCutoffSettings.daysAhead')}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </StyledTableRow>
                    {!isEventOrderPage && (
                      <StyledTableRow>
                        <TableCell>
                          <StyledBoldHeader>{t('editOrderPage.orderPageBuffer')}</StyledBoldHeader>
                        </TableCell>
                        <TableCell>
                          <FormTextField
                            width={100}
                            type="number"
                            isFieldUpdate
                            caption={t('editOrderPage.orderPageBufferCaption')}
                            fieldName="after_buffer_minutes"
                            disabled={formIsDisabled}
                          />
                        </TableCell>
                      </StyledTableRow>
                    )}
                    <StyledTableRow>
                      <TableCell>
                        <StyledBoldHeader>{t('editOrderPage.orderPageBufferDuration')}</StyledBoldHeader>
                      </TableCell>
                      <TableCell>
                        <FormTextField
                          width={100}
                          isFieldUpdate
                          fieldName="duration"
                          disabled={formIsDisabled}
                          type="number"
                        />
                      </TableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <TableCell>
                        <StyledBoldHeader>{t('editOrderPage.waitlist')}</StyledBoldHeader>
                      </TableCell>
                      <TableCell>
                        <FormCheckbox
                          fieldName={'wait_list_enabled'}
                          disabled={formIsDisabled}
                          isFieldUpdate
                          label={t('editOrderPage.waitListEnabled')}
                        />
                      </TableCell>
                    </StyledTableRow>
                    {isEventOrderPage && (
                      <>
                        <StyledTableRow>
                          <TableCell>
                            <StyledBoldHeader>{t('editOrderPage.defaultTime')}</StyledBoldHeader>
                          </TableCell>
                          <TableCell>
                            <FormTimePicker
                              isFieldUpdate
                              fieldName="default_time"
                              disabled={formIsDisabled}
                              width={140}
                            />
                          </TableCell>
                        </StyledTableRow>
                        <StyledTableRow>
                          <TableCell>
                            <StyledBoldHeader>{t('editOrderPage.defaultTicketsAmount')}</StyledBoldHeader>
                          </TableCell>
                          <TableCell>
                            <FormTextField
                              isFieldUpdate
                              type="number"
                              width={100}
                              fieldName="default_tickets_amount"
                              disabled={formIsDisabled}
                            />
                          </TableCell>
                        </StyledTableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
